import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

const KEY = 'quotes';

// ── queries ───────────────────────────────────────────────────────────────────

export function useQuotes(dealId, componentId) {
  return useQuery({
    queryKey: [KEY, dealId, componentId],
    queryFn: () => {
      const params = { deal_id: dealId };
      if (componentId) params.component_id = componentId;
      return api.get('/quotes', { params }).then((r) => r.data);
    },
    enabled: !!dealId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useQuote(id) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: () => api.get(`/quotes/${id}`).then((r) => r.data),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ── quote CRUD ────────────────────────────────────────────────────────────────

export function useCreateQuote(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/quotes', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

export function useUpdateQuote(id, dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch(`/quotes/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, id] });
      qc.invalidateQueries({ queryKey: [KEY, dealId] });
    },
  });
}

export function useDeleteQuote(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/quotes/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, dealId] }),
  });
}

export function usePushToComponent(quoteId, dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/quotes/${quoteId}/push-to-components`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, quoteId] });
      qc.invalidateQueries({ queryKey: ['deals', dealId] });
    },
  });
}

// ── hardware items ────────────────────────────────────────────────────────────

export function useAddHardwareItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/quotes/${quoteId}/hardware-items`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

export function useUpdateHardwareItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/quotes/${quoteId}/hardware-items/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

export function useDeleteHardwareItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/quotes/${quoteId}/hardware-items/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

// ── CDR upload ────────────────────────────────────────────────────────────────

export function useUploadCdr(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, sourceSystem }) => {
      const form = new FormData();
      form.append('cdr', file);
      if (sourceSystem) form.append('source_system', sourceSystem);
      return api.post(`/quotes/${quoteId}/cdr-upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

// ── phone items ───────────────────────────────────────────────────────────────

export function useAddPhoneItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/quotes/${quoteId}/phone-items`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

export function useUpdatePhoneItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/quotes/${quoteId}/phone-items/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

export function useDeletePhoneItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/quotes/${quoteId}/phone-items/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

// ── internet items ────────────────────────────────────────────────────────────

export function useAddInternetItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/quotes/${quoteId}/internet-items`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

export function useUpdateInternetItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/quotes/${quoteId}/internet-items/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

export function useDeleteInternetItem(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/quotes/${quoteId}/internet-items/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

// ── MSA plan pricing ──────────────────────────────────────────────────────────

export function useMsaPricing() {
  return useQuery({
    queryKey: ['msa-pricing'],
    queryFn: () => api.get('/quotes/msa-pricing').then((r) => r.data),
    staleTime: 30 * 60 * 1000,
  });
}

// ── MSA lines ─────────────────────────────────────────────────────────────────

export function useAddMsaLine(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/quotes/${quoteId}/msa-lines`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

export function useUpdateMsaLine(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/quotes/${quoteId}/msa-lines/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

export function useDeleteMsaLine(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/quotes/${quoteId}/msa-lines/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, quoteId] }),
  });
}

// ── discount approval ─────────────────────────────────────────────────────────

export function useRequestApproval(quoteId, dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/quotes/${quoteId}/request-approval`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, quoteId] });
      qc.invalidateQueries({ queryKey: [KEY, dealId] });
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
}

export function useApproveDiscount(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note) => api.post(`/quotes/${quoteId}/approve-discount`, { note }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, quoteId] });
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
}

export function useRejectDiscount(quoteId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note) => api.post(`/quotes/${quoteId}/reject-discount`, { note }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, quoteId] });
      qc.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });
}

export function usePendingApprovals(options = {}) {
  return useQuery({
    queryKey: ['pending-approvals'],
    queryFn: () => api.get('/quotes/pending-approvals').then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}
